(() => {
  const initializeMenu = (root = document) => {
    root.querySelectorAll('[data-lelo-menu]').forEach((menu) => {
      if (menu.dataset.leloInitialized === 'true') return;

      const trigger = menu.querySelector(':scope > summary');
      if (!trigger) return;
      const drawer = menu.querySelector(':scope > .lelo-menu-drawer');
      const closeButton = menu.querySelector(':scope > [data-lelo-menu-close]');
      let closeTimer;
      let wasOpen = menu.open;

      const finishClose = (restoreFocus = false) => {
        window.clearTimeout(closeTimer);
        menu.classList.remove('is-closing');
        menu.open = false;
        if (restoreFocus) window.requestAnimationFrame(() => trigger.focus());
      };

      const requestClose = ({ restoreFocus = false, immediate = false } = {}) => {
        if (!menu.open) return;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (immediate || reducedMotion || !drawer) {
          finishClose(restoreFocus);
          return;
        }
        if (menu.classList.contains('is-closing')) return;

        menu.classList.add('is-closing');
        document.dispatchEvent(new CustomEvent('lelo:menu-closing'));
        const onAnimationEnd = (event) => {
          if (event.target !== drawer || event.animationName !== 'leloMenuDrawerOut') return;
          drawer.removeEventListener('animationend', onAnimationEnd);
          finishClose(restoreFocus);
        };
        drawer.addEventListener('animationend', onAnimationEnd);
        closeTimer = window.setTimeout(() => {
          drawer.removeEventListener('animationend', onAnimationEnd);
          finishClose(restoreFocus);
        }, 380);
      };

      menu.leloRequestClose = requestClose;
      trigger.addEventListener('click', (event) => {
        if (!menu.open) return;
        event.preventDefault();
        requestClose();
      });
      closeButton?.addEventListener('click', () => requestClose());

      const syncState = () => {
        if (!menu.open) menu.classList.remove('is-closing');
        trigger.setAttribute('aria-expanded', String(menu.open));
        document.documentElement.classList.toggle('lelo-menu-open', menu.open);
        document.dispatchEvent(new CustomEvent('lelo:menu-toggle', { detail: { open: menu.open } }));
        if (menu.open && !wasOpen) {
          window.setTimeout(() => menu.querySelector('.lelo-menu-drawer a, .lelo-menu-drawer summary, .lelo-menu-drawer button')?.focus(), 0);
        }
        if (!menu.open) menu.querySelectorAll('.lelo-menu-submenu[open]').forEach((submenu) => submenu.removeAttribute('open'));
        wasOpen = menu.open;
      };

      menu.addEventListener('toggle', syncState);
      menu.addEventListener('keydown', (event) => {
        if (!menu.open) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          requestClose({ restoreFocus: true });
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = [...menu.querySelectorAll(':scope > summary, .lelo-menu-drawer a[href], .lelo-menu-drawer summary, .lelo-menu-drawer button:not([disabled])')]
          .filter((element) => element.getClientRects().length);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      menu.querySelectorAll('.lelo-menu-submenu').forEach((submenu) => {
        submenu.addEventListener('toggle', () => {
          if (!submenu.open) return;
          menu.querySelectorAll('.lelo-menu-submenu[open]').forEach((sibling) => {
            if (sibling !== submenu) sibling.removeAttribute('open');
          });
        });
      });

      document.addEventListener('click', (event) => {
        if (menu.open && !menu.contains(event.target)) requestClose();
      });

      menu.dataset.leloInitialized = 'true';
      syncState();
    });
  };

  const initializeSearch = (root = document) => {
    const dialog = root.querySelector?.('[data-lelo-search-dialog]') || document.querySelector('[data-lelo-search-dialog]');
    if (!dialog || dialog.dataset.leloSearchInitialized === 'true') return;
    let opener = null;
    const input = dialog.querySelector('input[name="q"]');
    const close = () => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
    };

    document.querySelectorAll('[data-lelo-search-open]').forEach((button) => {
      if (button.dataset.leloSearchOpenerInitialized === 'true') return;
      button.addEventListener('click', () => {
        opener = button;
        const menu = button.closest('[data-lelo-menu]');
        if (menu) {
          if (typeof menu.leloRequestClose === 'function') menu.leloRequestClose({ immediate: true });
          else menu.removeAttribute('open');
        }
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        window.setTimeout(() => input?.focus(), 0);
      });
      button.dataset.leloSearchOpenerInitialized = 'true';
    });
    dialog.querySelector('[data-lelo-search-close]')?.addEventListener('click', close);
    dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
    dialog.addEventListener('close', () => opener?.focus());
    dialog.dataset.leloSearchInitialized = 'true';
  };

  const initializeAgeGate = (root = document) => {
    const dialog = root.querySelector?.('[data-lelo-age-gate]') || document.querySelector('[data-lelo-age-gate]');
    if (!dialog || dialog.dataset.leloAgeInitialized === 'true') return;

    const storageKey = 'xxoo-age-confirmed';
    const confirmButton = dialog.querySelector('[data-lelo-age-confirm]');
    const declineButton = dialog.querySelector('[data-lelo-age-decline]');
    const hasConfirmed = () => {
      try {
        return window.sessionStorage.getItem(storageKey) === 'true';
      } catch (_error) {
        return false;
      }
    };
    const rememberConfirmation = () => {
      try {
        window.sessionStorage.setItem(storageKey, 'true');
      } catch (_error) {
        // Browsers that block storage still allow the visitor to continue this page view.
      }
    };
    const open = () => {
      if (hasConfirmed() || dialog.open) return;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      window.setTimeout(() => confirmButton?.focus(), 0);
    };
    const close = () => {
      rememberConfirmation();
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
    };

    confirmButton?.addEventListener('click', close);
    declineButton?.addEventListener('click', () => {
      window.location.replace(declineButton.dataset.declineUrl?.trim() || 'https://www.google.com');
    });
    dialog.addEventListener('cancel', (event) => event.preventDefault());
    dialog.dataset.leloAgeInitialized = 'true';
    open();
  };

  const initializeStickyHeader = (root = document) => {
    const header = root.querySelector?.('.lelo-site-header') || document.querySelector('.lelo-site-header');
    if (!header || header.dataset.leloStickyInitialized === 'true') return;

    const announcement = document.querySelector('.lelo-announcement');
    const menu = header.querySelector('[data-lelo-menu]');
    const heroSelectors = [
      '.lelo-hero',
      '.lelo-product-hero',
      '.lelo-fallback-hero',
      '.collection-hero--lelo-media',
      '.lelo-policy-hero',
      '.lelo-cart-empty-hero',
      '.lelo-contact-hero',
      '.lelo-about__hero',
      '.lelo-not-found',
      '.lelo-imported-page .block-inline-blockhero-block',
      '.lelo-imported-page .video-style.block-inline-blockhero-block',
    ].join(',');
    let lastScrollY = Math.max(0, window.scrollY);
    let frameRequested = false;

    const syncHeader = () => {
      frameRequested = false;
      const scrollY = Math.max(0, window.scrollY);
      const announcementBottom = announcement ? Math.max(0, announcement.getBoundingClientRect().bottom) : 0;
      const menuOpen = Boolean(menu?.open);
      const headerHasFocus = header.contains(document.activeElement);
      const directionDelta = scrollY - lastScrollY;
      const headerHeight = header.getBoundingClientRect().height || 85;
      const hero = document.querySelector(heroSelectors);
      const heroBottom = hero ? hero.getBoundingClientRect().bottom + scrollY : 0;

      header.style.setProperty('--lelo-header-top', `${menuOpen ? 0 : Math.round(announcementBottom)}px`);
      header.classList.toggle('is-stuck', scrollY > 1 && announcementBottom <= 1);
      header.classList.toggle('is-menu-active', menuOpen);
      header.classList.toggle('is-solid', menuOpen || !hero || scrollY + headerHeight >= heroBottom - 1);

      if (menuOpen || headerHasFocus || scrollY < 8) {
        header.classList.remove('is-hidden');
      } else if (directionDelta > 5 && announcementBottom <= 1) {
        header.classList.add('is-hidden');
      } else if (directionDelta < -5) {
        header.classList.remove('is-hidden');
      }

      lastScrollY = scrollY;
    };

    const requestSync = () => {
      if (frameRequested) return;
      frameRequested = true;
      window.requestAnimationFrame(syncHeader);
    };

    window.addEventListener('scroll', requestSync, { passive: true });
    window.addEventListener('resize', requestSync, { passive: true });
    document.addEventListener('lelo:menu-toggle', requestSync);
    header.addEventListener('focusin', requestSync);
    header.dataset.leloStickyInitialized = 'true';
    syncHeader();
  };

  const initializeFooter = (root = document) => {
    root.querySelectorAll('.lelo-footer-nav').forEach((column) => {
      if (column.dataset.leloFooterInitialized === 'true') return;
      const trigger = column.querySelector('.lelo-footer-toggle');
      if (!trigger) return;
      trigger.addEventListener('click', () => {
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        const expanded = column.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', String(expanded));
      });
      const resetDesktopState = () => {
        if (window.matchMedia('(max-width: 749px)').matches) return;
        column.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      };
      window.addEventListener('resize', resetDesktopState, { passive: true });
      column.dataset.leloFooterInitialized = 'true';
    });
  };

  const initializeReducedMotionMedia = (root = document) => {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    root.querySelectorAll('.lelo-home video[autoplay]').forEach((video) => {
      video.pause();
      video.removeAttribute('autoplay');
    });
  };

  const initializeQuiz = (root = document) => {
    const dialog = root.querySelector('[data-lelo-quiz]');
    if (!dialog || dialog.dataset.leloQuizInitialized === 'true') return;

    const form = dialog.querySelector('[data-lelo-quiz-form]');
    const result = dialog.querySelector('[data-lelo-quiz-result]');
    const resultLink = dialog.querySelector('[data-lelo-quiz-link]');
    const openers = document.querySelectorAll('[data-lelo-quiz-open]');
    const closer = dialog.querySelector('[data-lelo-quiz-close]');

    openers.forEach((opener) => opener.addEventListener('click', () => {
      form?.reset();
      form?.querySelectorAll('fieldset, button[type="submit"]').forEach((element) => { element.hidden = false; });
      if (result) result.hidden = true;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      window.setTimeout(() => form?.querySelector('input, button')?.focus(), 0);
    }));

    closer?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const data = new FormData(form);
      const audience = data.get('audience');
      const feature = data.get('feature');
      const destination = feature === 'app'
        ? dialog.dataset.resultApp
        : feature === 'new'
          ? dialog.dataset.resultNew
          : audience === 'women'
            ? dialog.dataset.resultWomen
            : audience === 'men'
              ? dialog.dataset.resultMen
              : audience === 'couples'
                ? dialog.dataset.resultCouples
                : dialog.dataset.resultDefault;

      if (resultLink) resultLink.href = destination;
      form.querySelectorAll('fieldset, button[type="submit"]').forEach((element) => { element.hidden = true; });
      if (result) result.hidden = false;
    });

    dialog.dataset.leloQuizInitialized = 'true';
  };

  const initializeCampaignVideos = (root = document) => {
    const dialog = root.querySelector?.('[data-lelo-video-dialog]') || document.querySelector('[data-lelo-video-dialog]');
    if (!dialog || dialog.dataset.leloVideoInitialized === 'true') return;
    const video = dialog.querySelector('video');
    let opener = null;
    const reset = () => {
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
      opener?.focus();
    };
    const close = () => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else {
        dialog.removeAttribute('open');
        reset();
      }
    };
    document.querySelectorAll('[data-lelo-video-open]').forEach((button) => {
      if (button.dataset.leloVideoOpenerInitialized === 'true') return;
      button.addEventListener('click', () => {
        const videoSrc = button.dataset.leloVideoSrc;
        if (!videoSrc || !video) return;
        opener = button;
        const videoTitle = button.dataset.leloVideoTitle || 'LELO video';
        video.src = videoSrc;
        video.title = videoTitle;
        video.setAttribute('aria-label', videoTitle);
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        video.play().catch(() => {});
        window.setTimeout(() => dialog.querySelector('[data-lelo-video-close]')?.focus(), 0);
      });
      button.dataset.leloVideoOpenerInitialized = 'true';
    });
    dialog.querySelector('[data-lelo-video-close]')?.addEventListener('click', close);
    dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
    dialog.addEventListener('close', reset);
    dialog.dataset.leloVideoInitialized = 'true';
  };

  const initializeInlineVideos = (root = document) => {
    const videos = root.querySelectorAll('[data-lelo-inline-video]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    videos.forEach((video) => {
      if (video.dataset.leloInlineVideoInitialized === 'true') return;
      video.muted = true;
      video.dataset.leloInlineVideoInitialized = 'true';
      const ensureSource = () => {
        if (!video.getAttribute('src') && video.dataset.leloInlineVideoSrc) {
          video.src = video.dataset.leloInlineVideoSrc;
        }
      };

      if (reducedMotion) {
        video.removeAttribute('autoplay');
        video.controls = true;
      }
      if (!('IntersectionObserver' in window)) {
        ensureSource();
        if (!reducedMotion) video.play().catch(() => {});
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            ensureSource();
            if (!reducedMotion) entry.target.play().catch(() => {});
          }
          else entry.target.pause();
        });
      }, { rootMargin: '180px 0px', threshold: 0.08 });
      observer.observe(video);
    });
  };

  const initializeProductCampaign = (root = document) => {
    root.querySelectorAll('[data-lelo-product-buy]').forEach((button) => {
      if (button.dataset.leloProductBuyInitialized === 'true') return;
      button.addEventListener('click', () => {
        const purchase = document.querySelector('product-info');
        if (!purchase) return;
        purchase.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        const firstControl = purchase.querySelector('button, input, select, a[href]');
        window.setTimeout(() => firstControl?.focus({ preventScroll: true }), 450);
      });
      button.dataset.leloProductBuyInitialized = 'true';
    });
  };

  const initializeWishlist = (root = document) => {
    const storageKey = 'lelo-wishlist-products';
    let savedHandles = [];
    try {
      savedHandles = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(savedHandles)) savedHandles = [];
    } catch (_error) {
      savedHandles = [];
    }

    root.querySelectorAll('[data-lelo-wishlist]').forEach((button) => {
      if (button.dataset.leloWishlistInitialized === 'true') return;
      const handle = button.dataset.productHandle;
      if (!handle) return;

      const syncLabel = (selected) => {
        const card = button.closest('.card-wrapper, .lelo-product-card');
        const cardTitle = card?.querySelector('.card__heading, .lelo-product-title')?.textContent?.trim() || 'product';
        button.setAttribute('aria-pressed', String(selected));
        button.setAttribute('aria-label', `${selected ? 'Remove' : 'Add'} ${cardTitle} ${selected ? 'from' : 'to'} wishlist`);
      };

      syncLabel(savedHandles.includes(handle));
      button.addEventListener('click', () => {
        const selected = button.getAttribute('aria-pressed') !== 'true';
        savedHandles = selected
          ? [...new Set([...savedHandles, handle])]
          : savedHandles.filter((savedHandle) => savedHandle !== handle);
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(savedHandles));
        } catch (_error) {
          // The control still exposes its current pressed state if storage is unavailable.
        }
        document.querySelectorAll(`[data-lelo-wishlist][data-product-handle="${CSS.escape(handle)}"]`).forEach((matchingButton) => {
          matchingButton.setAttribute('aria-pressed', String(selected));
        });
        syncLabel(selected);
      });
      button.dataset.leloWishlistInitialized = 'true';
    });
  };

  let productPurchaseVariantUnsubscribe;
  const initializeProductPurchaseVariant = (root = document) => {
    const purchase = root.querySelector?.('[data-lelo-product-purchase]');
    if (!purchase || purchase.dataset.leloVariantSyncInitialized === 'true') return;

    let variants = [];
    try {
      variants = JSON.parse(purchase.querySelector('[data-lelo-product-variants]')?.textContent || '[]');
    } catch (error) {
      console.error('Unable to read LELO product variants', error);
      return;
    }
    if (!variants.length || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    productPurchaseVariantUnsubscribe?.();
    productPurchaseVariantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (!purchase.isConnected) return;
      const selectedVariant = variants.find((variant) => String(variant.id) === String(event.data?.variant?.id));
      if (!selectedVariant) return;

      const variantInput = purchase.querySelector('input[name="id"]');
      const variantTitle = purchase.querySelector('[data-lelo-purchase-variant-title]');
      const variantPrice = purchase.querySelector('[data-lelo-purchase-variant-price]');
      const submitButton = purchase.querySelector('button[name="add"]');
      const submitLabel = purchase.querySelector('[data-lelo-purchase-button-label]');

      if (variantInput) {
        variantInput.value = selectedVariant.id;
        variantInput.disabled = !selectedVariant.available;
      }
      if (variantTitle) variantTitle.textContent = selectedVariant.title;
      if (variantPrice) variantPrice.textContent = selectedVariant.price;
      if (submitButton) submitButton.disabled = !selectedVariant.available;
      if (submitLabel) submitLabel.textContent = selectedVariant.available ? 'Add to cart' : 'Sold out';
    });
    purchase.dataset.leloVariantSyncInitialized = 'true';
  };

  const initializeImportedPages = (root = document) => {
    root.querySelectorAll('[data-lelo-imported-page]').forEach((pageRoot) => {
      if (pageRoot.dataset.leloImportedInitialized === 'true') return;

      const groups = [
        {
          label: 'Company',
          links: [
            ['About LELO', '/pages/about-us'],
            ['Company information', '/pages/company-information'],
            ['Careers', '/pages/careers'],
            ['Affiliates', '/pages/affiliates'],
            ['Press room', '/pages/press-room'],
            ['Industry awards', '/pages/industry-awards'],
          ],
        },
        {
          label: 'Support',
          links: [['Contact support', '/pages/support-contact']],
        },
        {
          label: 'FAQ',
          links: [
            ['General FAQs', '/pages/general-faqs'],
            ['Product FAQs', '/pages/product-faqs'],
            ['Shopping FAQs', '/pages/shopping-faqs'],
          ],
        },
        {
          label: 'Environmental labels',
          links: [['Environmental labels', '/pages/environmental-labels']],
        },
      ];
      const currentPath = window.location.pathname.replace(/\/$/, '');
      const desktopMenu = pageRoot.querySelector('.basic-page-menu');

      if (desktopMenu && !desktopMenu.children.length) {
        groups.forEach((group) => {
          const details = document.createElement('details');
          details.className = 'lelo-basic-nav-group';

          const summary = document.createElement('summary');
          summary.textContent = group.label;
          details.appendChild(summary);

          const list = document.createElement('ul');
          group.links.forEach(([label, href]) => {
            const item = document.createElement('li');
            const link = document.createElement('a');
            link.href = href;
            link.textContent = label;
            if (href === currentPath) link.setAttribute('aria-current', 'page');
            item.appendChild(link);
            list.appendChild(item);
          });
          details.appendChild(list);
          desktopMenu.appendChild(details);
        });
      }

      pageRoot.querySelectorAll('.ckeditor-accordion-container dl').forEach((list, listIndex) => {
        [...list.children].forEach((node, nodeIndex) => {
          if (node.tagName !== 'DT') return;
          const trigger = node.querySelector('.ckeditor-accordion-toggler');
          const panel = node.nextElementSibling;
          if (!trigger || panel?.tagName !== 'DD') return;

          const panelId = `LeloFaq-${pageRoot.dataset.leloImportedPage}-${listIndex}-${nodeIndex}`;
          panel.id = panelId;
          panel.hidden = true;
          trigger.setAttribute('role', 'button');
          trigger.setAttribute('aria-controls', panelId);
          trigger.setAttribute('aria-expanded', 'false');

          const toggle = () => {
            const expanded = trigger.getAttribute('aria-expanded') === 'true';
            trigger.setAttribute('aria-expanded', String(!expanded));
            panel.hidden = expanded;
          };
          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            toggle();
          });
          trigger.addEventListener('keydown', (event) => {
            if (event.key !== ' ') return;
            event.preventDefault();
            toggle();
          });
        });
      });

      pageRoot.dataset.leloImportedInitialized = 'true';
    });
  };

  let revealObserver;
  const initializeMotion = (root = document) => {
    const selectors = [
      '.lelo-home > .lelo-intro',
      '.lelo-home > .lelo-quiz',
      '.lelo-home > .lelo-split',
      '.lelo-home > .lelo-video',
      '.lelo-home > .lelo-hero:not(.lelo-hero--primary)',
      '.lelo-home > .lelo-press',
      '.lelo-home .lelo-category-card',
      '.lelo-home .lelo-product-card',
      '.lelo-home .lelo-award',
      '.lelo-home .lelo-bundle-card',
      '.lelo-home .lelo-blog-card',
      '.lelo-imported-page .basic__body > .body > :is(.campaign_block, .heading_block, .story_block, .custom_block, .quotes_block, .lifestyle_block, .related-products)',
      '.lelo-collection-directory__item',
      '.template-blog .article-card-wrapper',
      '.lelo-volonte-card',
      '.template-product .lelo-campaign-intro > div',
      '.template-product .lelo-campaign-highlights__grid > picture',
      '.template-product .lelo-campaign-tech-list > li',
      '.template-product .lelo-campaign-how__grid > article',
      '.template-product .lelo-campaign-overlay > :is(h2, p)',
      '.template-product .lelo-campaign-story > :is(picture, div)',
      '.template-product .lelo-campaign-features > div > article',
      '.template-product .lelo-campaign-details__grid > :is(picture, .lelo-campaign-accordions)',
      '.template-product .lelo-campaign-app > div',
      '.template-product .lelo-campaign-iso > div',
      '.template-product .lelo-campaign-awards__grid > article',
      '.template-product .lelo-tor__intro > div',
      '.template-product .lelo-tor__highlights-grid > :is(picture, ol)',
      '.template-product .lelo-tor__steps > article',
      '.template-product .lelo-tor__overlay > h2',
      '.template-product .lelo-tor__features > div > article',
      '.template-product .lelo-tor__details-grid > :is(picture, .lelo-tor__accordions)',
      '.template-product .lelo-tor__app > div',
      '.template-product .lelo-tor__iso > div',
      '.template-product .lelo-tor__awards > div > article',
      '.template-product .lelo-fallback-intro > div',
      '.template-product .lelo-fallback-showcase > :is(.lelo-fallback-showcase__media, .lelo-fallback-showcase__copy)',
      '.template-product .lelo-fallback-gallery figure',
      '.template-product .lelo-fallback-details > :is(.lelo-campaign-kicker, h2, dl, details)',
      '.template-product .lelo-product-newsletter > div',
      ':is(.template-collection, .template-search) #product-grid > li',
      '.template-list-collections .collection-list__item',
      '.template-search .template-search__header',
      '.template-cart .cart-items > :is(thead, tbody)',
      '.template-policy .shopify-policy__body > *',
      '.lelo-contact-links > *',
    ].join(',');
    const candidates = [
      ...(root instanceof Element && root.matches(selectors) ? [root] : []),
      ...root.querySelectorAll(selectors),
    ];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!revealObserver && !reducedMotion && 'IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -9% 0px', threshold: 0.08 });
    }

    candidates.forEach((element, index) => {
      if (element.dataset.leloRevealInitialized === 'true') return;
      element.dataset.leloRevealInitialized = 'true';
      element.classList.add('lelo-reveal');
      element.style.setProperty('--lelo-reveal-delay', `${(index % 4) * 45}ms`);
      const inOpeningViewport = element.getBoundingClientRect().top < window.innerHeight * 0.92;
      if (reducedMotion || !revealObserver || inOpeningViewport) element.classList.add('is-visible');
      else revealObserver.observe(element);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeMenu();
      initializeSearch();
      initializeAgeGate();
      initializeStickyHeader();
      initializeFooter();
      initializeReducedMotionMedia();
      initializeQuiz();
      initializeProductCampaign();
      initializeWishlist();
      initializeProductPurchaseVariant();
      initializeCampaignVideos();
      initializeInlineVideos();
      initializeImportedPages();
      initializeMotion();
    }, { once: true });
  } else {
    initializeMenu();
    initializeSearch();
    initializeAgeGate();
    initializeStickyHeader();
    initializeFooter();
    initializeReducedMotionMedia();
    initializeQuiz();
    initializeProductCampaign();
    initializeWishlist();
    initializeProductPurchaseVariant();
    initializeCampaignVideos();
    initializeInlineVideos();
    initializeImportedPages();
    initializeMotion();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeMenu(event.target);
    initializeSearch(event.target);
    initializeAgeGate(event.target);
    initializeStickyHeader(event.target);
    initializeFooter(event.target);
    initializeReducedMotionMedia(event.target);
    initializeQuiz(event.target);
    initializeProductCampaign(event.target);
    initializeWishlist(event.target);
    initializeProductPurchaseVariant(event.target);
    initializeCampaignVideos(event.target);
    initializeInlineVideos(event.target);
    initializeImportedPages(event.target);
    initializeMotion(event.target);
  });
})();
